/**
 * UserPicker — FEAT-021 / S-178 + S-179.
 *
 * A reusable multi-select list of EXISTING tenant users, backed by
 * GET /memberships/candidates. Used in two places:
 *   - TeamView "Add existing user" (S-178) — excludes users already in
 *     the target workspace via `excludeAgencyclientId`.
 *   - WorkspacesView create flow (S-179) — no exclusion (the workspace
 *     doesn't exist yet), so every tenant user is selectable.
 *
 * The picker is presentational over a query: it renders checkboxes and
 * reports the selected set up to the parent. Role assignment and the
 * POST /memberships calls live in the parent so each surface can sequence
 * them with its own success/refresh behavior.
 */

import { useApiQuery, ApiError } from "@/lib/api";

export interface CandidateUser {
  clerk_user_id: string;
  user_email: string | null;
  user_name: string | null;
  memberships: Array<{ agencyclient_id: number; role: string }>;
}

interface CandidatesResponse {
  data: CandidateUser[];
  meta: { total: number };
}

interface UserPickerProps {
  /** When set, users already in this workspace are filtered out server-side. */
  excludeAgencyclientId?: number;
  selectedIds: Set<string>;
  onToggle: (clerkUserId: string) => void;
  /** Optional empty-state copy override. */
  emptyLabel?: string;
}

export function UserPicker({
  excludeAgencyclientId,
  selectedIds,
  onToggle,
  emptyLabel,
}: UserPickerProps) {
  const qs =
    excludeAgencyclientId != null
      ? `?exclude_agencyclient_id=${excludeAgencyclientId}`
      : "";
  const { data, isLoading, error } = useApiQuery<CandidatesResponse>(
    ["membership-candidates", excludeAgencyclientId ?? "all"],
    `/memberships/candidates${qs}`
  );

  if (isLoading) {
    return <div className="state-loading">Loading teammates…</div>;
  }

  if (error) {
    return (
      <div className="state-error">
        {error instanceof ApiError && error.status === 403
          ? "You don't have permission to list teammates."
          : (error.message ?? "Could not load teammates.")}
      </div>
    );
  }

  const users = data?.data ?? [];
  if (users.length === 0) {
    return (
      <div className="team-section-empty">
        {emptyLabel ??
          "No other teammates available. Invite someone first."}
      </div>
    );
  }

  return (
    <ul className="user-picker-list">
      {users.map((u) => {
        const id = u.clerk_user_id;
        const checked = selectedIds.has(id);
        const label = u.user_name || u.user_email || "(unknown user)";
        return (
          <li key={id} className="user-picker-item">
            <label className="user-picker-row">
              <input
                type="checkbox"
                checked={checked}
                onChange={() => onToggle(id)}
              />
              <span className="user-picker-meta">
                <span className="user-picker-name">{label}</span>
                {u.user_email && u.user_name && (
                  <span className="user-picker-email">{u.user_email}</span>
                )}
                <span className="user-picker-count">
                  {u.memberships.length} workspace
                  {u.memberships.length === 1 ? "" : "s"}
                </span>
              </span>
            </label>
          </li>
        );
      })}
    </ul>
  );
}
